/* eslint-disable jira/filename-case, max-classes-per-file */
import ExtensibleError from '@jira/platform__utils-extensible-error/src/index.tsx';

export const TYPE_ERROR_NAME = 'TypeError';
export const ABORT_ERROR_NAME = 'AbortError';
export const VALIDATION_ERROR_NAME = 'ValidationError';
export const FETCH_ERROR_NAME = 'FetchError';
export const RELAY_NETWORK_ERRORS_NAME = 'RelayNetwork';

/**
 * One field validation error
 */
export type FieldValidationError = {
	field: string;
	error: string;
};

/**
 * Request validation error
 */
export class ValidationError extends ExtensibleError {
	errors: FieldValidationError[];

	statusCode: number | undefined;

	skipSentry?: boolean;

	traceId?: string;

	originalResponse?: unknown;

	constructor(
		message: string,
		errors: FieldValidationError[] = [],
		statusCode?: number,
		traceId?: string,
		originalResponse?: unknown,
	) {
		super(message, { cause: errors[0] });
		// Setting name to a hardcoded string so it doesn't get obfuscated. This is used for my poor man's instanceof
		// check in common/util/logging/log.js.
		this.name = VALIDATION_ERROR_NAME;
		this.errors = errors;
		this.statusCode = statusCode;
		this.skipSentry = true;
		this.traceId = traceId;
		this.originalResponse = originalResponse;
	}
}

export const isValidationError = (error: ValidationError | Error): error is ValidationError =>
	error.name === VALIDATION_ERROR_NAME;

/**
 * Generic HTTP error for calling Fetch.
 * <p>
 * This would be used if there isn't a specific HTTP error that you are expecting to receive from
 * the server. For example, a Bad Gateway error (502).
 */
export default class FetchError extends ExtensibleError {
	skipSentry: boolean;

	statusCode: number;

	traceId: string | undefined;

	originalResponse: Response | undefined;

	constructor(statusCode: number, message?: string, traceId?: string, originalResponse?: Response) {
		super(message || `Fetch call failed with status code: ${statusCode}`);
		this.name = FETCH_ERROR_NAME;
		this.statusCode = statusCode;
		this.originalResponse = originalResponse;
		this.skipSentry = statusCode === 401;

		if (traceId) {
			this.traceId = traceId;
		}
	}
}

export const isFetchError = (error: unknown): error is FetchError =>
	error instanceof FetchError && error.name === FETCH_ERROR_NAME;

/**
 * Relay error extensions structure
 */
export type RelayErrorExtensions = {
	errorSource?: string;
	statusCode?: number;
	classification?: string;
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	[key: string]: any;
};

/**
 * Relay error structure
 */
export type RelayError = {
	message: string;
	extensions?: RelayErrorExtensions;
};

/**
 * Relay error source structure
 */
export type RelayErrorSource = {
	errors?: RelayError[];
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	operation?: { [key: string]: any };
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	variables?: { [key: string]: any };
};

/**
 * Relay error structure
 */
export type RelayNetworkErrors = {
	name: string;
	type: string;
	source?: RelayErrorSource;
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	[key: string]: any;
};

export const isRelayNetworkError = (
	error: RelayNetworkErrors | Error,
): error is RelayNetworkErrors => error.name === RELAY_NETWORK_ERRORS_NAME;
